/**
 * IntentPathCard — Onboarding State 2 intent-based accordion
 *
 * Replaces the ERP-jargon onboarding checklist with 3 merchant-goal paths:
 * "Start selling", "Chat with my customers", "Organize my contacts".
 * Single-selection accordion: tapping a path expands it to show steps.
 */

import { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, Circle, ShoppingBag, MessageCircle, Users, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { IntentPath } from "@/hooks/useDashboardData";

const PATH_ICONS: Record<string, typeof ShoppingBag> = {
  selling: ShoppingBag,
  chat: MessageCircle,
  contacts: Users,
};

interface IntentPathLabels {
  question: string;
  paths: Record<string, { title: string; description: string }>;
  steps: Record<string, { title: string; description: string }>;
  exploreAll: string;
  completed: string;
}

interface IntentPathCardProps {
  paths: IntentPath[];
  selectedPathId: string | null;
  onSelectPath: (pathId: string) => void;
  labels: IntentPathLabels;
}

export function IntentPathCard({ paths, selectedPathId, onSelectPath, labels }: IntentPathCardProps) {
  const reducedMotion = useReducedMotion();

  return (
    <div className="space-y-4">
      {/* Question header */}
      <h2 className="text-lg font-semibold text-foreground">
        {labels.question}
      </h2>

      {/* Path cards */}
      <div className="space-y-3">
        {paths.map((path) => (
          <PathItem
            key={path.id}
            path={path}
            isSelected={selectedPathId === path.id}
            onSelect={() => onSelectPath(path.id)}
            labels={labels}
            reducedMotion={reducedMotion ?? false}
          />
        ))}
      </div>

      {/* Explore everything link */}
      <Link
        to="/tools"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mt-2"
      >
        {labels.exploreAll}
        <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

function PathItem({
  path,
  isSelected,
  onSelect,
  labels,
  reducedMotion,
}: {
  path: IntentPath;
  isSelected: boolean;
  onSelect: () => void;
  labels: IntentPathLabels;
  reducedMotion: boolean;
}) {
  const pathLabel = labels.paths[path.id];
  const Icon = PATH_ICONS[path.id] ?? ShoppingBag;
  const firstStepRef = useRef<HTMLLIElement>(null);

  // Focus management: when path expands, focus the first step
  useEffect(() => {
    if (isSelected && firstStepRef.current) {
      firstStepRef.current.focus();
    }
  }, [isSelected]);

  if (!pathLabel) return null;

  return (
    <div
      className={cn(
        "rounded border transition-colors duration-200",
        isSelected
          ? "border-blue-200 dark:border-blue-800 bg-card"
          : "border-border bg-card/80",
        path.allComplete && "border-green-200 dark:border-green-800"
      )}
    >
      {/* Path header — tap target */}
      <button
        onClick={onSelect}
        aria-expanded={isSelected}
        aria-controls={`path-steps-${path.id}`}
        className={cn(
          "w-full flex items-center gap-3 p-4 text-left",
          "rounded transition-colors",
          "hover:bg-accent",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
          "active:scale-[0.98] transition-transform duration-75"
        )}
      >
        <div
          className={cn(
            "flex items-center justify-center w-10 h-10 rounded-sm flex-shrink-0",
            path.allComplete
              ? "bg-green-100 dark:bg-green-900/30"
              : isSelected
                ? "bg-blue-100 dark:bg-blue-900/30"
                : "bg-muted"
          )}
        >
          {path.allComplete ? (
            <CheckCircle2 className="w-5 h-5 text-green-600" />
          ) : (
            <Icon
              className={cn(
                "w-5 h-5",
                isSelected ? "text-blue-600" : "text-muted-foreground"
              )}
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={cn(
            "text-sm font-medium",
            path.allComplete
              ? "text-green-700 dark:text-green-400"
              : "text-foreground"
          )}>
            {pathLabel.title}
            {path.allComplete && (
              <span className="ml-2 text-xs text-green-600 dark:text-green-400">
                {labels.completed}
              </span>
            )}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {pathLabel.description}
          </p>
        </div>
        <ChevronRight
          className={cn(
            "w-4 h-4 text-muted-foreground transition-transform duration-200",
            isSelected && "rotate-90"
          )}
        />
      </button>

      {/* Expanded steps */}
      <AnimatePresence initial={false}>
        {isSelected && (
          <motion.div
            id={`path-steps-${path.id}`}
            role="region"
            aria-label={pathLabel.title}
            initial={reducedMotion ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={reducedMotion ? undefined : { height: 0, opacity: 0 }}
            transition={{ duration: reducedMotion ? 0 : 0.25, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <ul className="px-4 pb-4 space-y-2">
              {path.steps.map((step, index) => {
                const stepLabel = labels.steps[step.id];
                if (!stepLabel) return null;
                return (
                  <li
                    key={step.id}
                    ref={index === 0 ? firstStepRef : undefined}
                    tabIndex={index === 0 ? -1 : undefined}
                    className="flex items-start gap-3 py-2"
                  >
                    {step.completed ? (
                      <CheckCircle2 className="w-4.5 h-4.5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Circle className="w-4.5 h-4.5 text-muted-foreground/50 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <Link
                        to={step.href}
                        className={cn(
                          "text-sm font-medium hover:underline",
                          step.completed
                            ? "text-muted-foreground"
                            : "text-foreground"
                        )}
                      >
                        {stepLabel.title}
                      </Link>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {stepLabel.description}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
